import React, { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import PageHeader from "@/components/PageHeader";
import { StatusBadge } from "@/components/StatusBadge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Plus, Trash2, Edit2, FileText, X } from "lucide-react";

const emptyInvoice = {
  client_name: "", client_email: "", client_address: "",
  items: [{ description: "", quantity: 1, rate: 0 }],
  subtotal: 0, tax_rate: 10, tax_amount: 0, total: 0,
  status: "draft", due_date: "", notes: ""
};

export default function InvoicesPage() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...emptyInvoice });

  const fetchInvoices = () => {
    axios.get(`${API}/invoices`).then(res => { setInvoices(res.data); setLoading(false); }).catch(() => setLoading(false));
  };
  useEffect(() => { fetchInvoices(); }, []);

  const calculateTotals = (items, taxRate) => {
    const subtotal = items.reduce((sum, item) => sum + (item.quantity * item.rate), 0);
    const taxAmount = subtotal * (taxRate / 100);
    return { subtotal, tax_amount: taxAmount, total: subtotal + taxAmount };
  };

  const updateItem = (idx, field, value) => {
    const newItems = [...form.items];
    newItems[idx] = { ...newItems[idx], [field]: field === "description" ? value : Number(value) };
    const totals = calculateTotals(newItems, form.tax_rate);
    setForm({ ...form, items: newItems, ...totals });
  };

  const addItem = () => {
    setForm({ ...form, items: [...form.items, { description: "", quantity: 1, rate: 0 }] });
  };

  const removeItem = (idx) => {
    if (form.items.length <= 1) return;
    const newItems = form.items.filter((_, i) => i !== idx);
    const totals = calculateTotals(newItems, form.tax_rate);
    setForm({ ...form, items: newItems, ...totals });
  };

  const handleSave = async () => {
    if (!form.client_name) { toast.error("Client name is required"); return; }
    try {
      if (editing) {
        await axios.put(`${API}/invoices/${editing.id}`, form);
        toast.success("Invoice updated");
      } else {
        await axios.post(`${API}/invoices`, form);
        toast.success("Invoice created");
      }
      setDialogOpen(false);
      setEditing(null);
      setForm({ ...emptyInvoice });
      fetchInvoices();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save invoice");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this invoice?")) return;
    try {
      await axios.delete(`${API}/invoices/${id}`);
      toast.success("Invoice deleted");
      fetchInvoices();
    } catch { toast.error("Failed to delete"); }
  };

  const openEdit = (inv) => {
    setEditing(inv);
    setForm({ ...inv, items: inv.items?.length ? inv.items : [{ description: "", quantity: 1, rate: 0 }] });
    setDialogOpen(true);
  };

  const openNew = () => {
    setEditing(null);
    setForm({ ...emptyInvoice, items: [{ description: "", quantity: 1, rate: 0 }] });
    setDialogOpen(true);
  };

  return (
    <div data-testid="invoices-page">
      <PageHeader title="Invoices" subtitle={`${invoices.length} invoices`}>
        <button
          data-testid="create-invoice-button"
          onClick={openNew}
          className="h-8 px-3 bg-orange-500 hover:bg-orange-600 text-white text-xs font-medium uppercase tracking-wider rounded-[2px] flex items-center gap-1.5 transition-colors active:scale-95"
        >
          <Plus className="w-3.5 h-3.5" /> New Invoice
        </button>
      </PageHeader>

      <div className="p-6">
        {loading ? (
          <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" /></div>
        ) : invoices.length === 0 ? (
          <div className="text-center py-20">
            <FileText className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
            <p className="text-zinc-400 text-sm">No invoices yet. Create your first invoice.</p>
          </div>
        ) : (
          <div className="border border-zinc-800 rounded-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-zinc-900/50 border-b border-zinc-800">
                  <th className="text-left px-4 py-2.5 text-[10px] text-zinc-500 uppercase tracking-wider font-medium">Invoice #</th>
                  <th className="text-left px-4 py-2.5 text-[10px] text-zinc-500 uppercase tracking-wider font-medium">Client</th>
                  <th className="text-left px-4 py-2.5 text-[10px] text-zinc-500 uppercase tracking-wider font-medium">Total</th>
                  <th className="text-left px-4 py-2.5 text-[10px] text-zinc-500 uppercase tracking-wider font-medium">Status</th>
                  <th className="text-left px-4 py-2.5 text-[10px] text-zinc-500 uppercase tracking-wider font-medium">Due Date</th>
                  <th className="text-right px-4 py-2.5 text-[10px] text-zinc-500 uppercase tracking-wider font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((inv) => (
                  <tr key={inv.id} className="border-b border-zinc-800/30 hover:bg-zinc-800/20" data-testid={`invoice-row-${inv.id}`}>
                    <td className="px-4 py-2.5 font-mono text-xs text-orange-400">{inv.invoice_number}</td>
                    <td className="px-4 py-2.5 text-zinc-200">{inv.client_name}</td>
                    <td className="px-4 py-2.5 font-mono text-xs text-zinc-300">${(inv.total || 0).toLocaleString()}</td>
                    <td className="px-4 py-2.5"><StatusBadge status={inv.status} /></td>
                    <td className="px-4 py-2.5 font-mono text-xs text-zinc-400">{inv.due_date || "—"}</td>
                    <td className="px-4 py-2.5 text-right">
                      <button data-testid={`edit-invoice-${inv.id}`} onClick={() => openEdit(inv)} className="text-zinc-500 hover:text-zinc-200 p-1 mr-1"><Edit2 className="w-3.5 h-3.5" /></button>
                      <button data-testid={`delete-invoice-${inv.id}`} onClick={() => handleDelete(inv.id)} className="text-zinc-500 hover:text-red-400 p-1"><Trash2 className="w-3.5 h-3.5" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl bg-[#18181B] border-zinc-800 text-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg font-bold tracking-tight">
              {editing ? "Edit Invoice" : "New Invoice"}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 mt-2">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-zinc-400 text-xs uppercase tracking-wider">Client Name *</Label>
                <Input data-testid="invoice-client-name" value={form.client_name} onChange={e => setForm({ ...form, client_name: e.target.value })}
                  className="mt-1 bg-zinc-900 border-zinc-700 text-white rounded-[2px]" />
              </div>
              <div>
                <Label className="text-zinc-400 text-xs uppercase tracking-wider">Client Email</Label>
                <Input data-testid="invoice-client-email" value={form.client_email} onChange={e => setForm({ ...form, client_email: e.target.value })}
                  className="mt-1 bg-zinc-900 border-zinc-700 text-white rounded-[2px]" />
              </div>
            </div>
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Client Address</Label>
              <Input data-testid="invoice-client-address" value={form.client_address} onChange={e => setForm({ ...form, client_address: e.target.value })}
                className="mt-1 bg-zinc-900 border-zinc-700 text-white rounded-[2px]" />
            </div>

            {/* Line Items */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-zinc-400 text-xs uppercase tracking-wider">Line Items</Label>
                <button data-testid="add-invoice-item" onClick={addItem} className="text-orange-500 text-xs hover:text-orange-400 flex items-center gap-1"><Plus className="w-3 h-3" /> Add Item</button>
              </div>
              <div className="space-y-2">
                {form.items.map((item, idx) => (
                  <div key={idx} className="grid grid-cols-12 gap-2 items-center">
                    <Input data-testid={`item-desc-${idx}`} placeholder="Description" value={item.description} onChange={e => updateItem(idx, "description", e.target.value)}
                      className="col-span-6 bg-zinc-900 border-zinc-700 text-white text-xs rounded-[2px]" />
                    <Input data-testid={`item-qty-${idx}`} type="number" placeholder="Qty" value={item.quantity} onChange={e => updateItem(idx, "quantity", e.target.value)}
                      className="col-span-2 bg-zinc-900 border-zinc-700 text-white text-xs rounded-[2px]" />
                    <Input data-testid={`item-rate-${idx}`} type="number" placeholder="Rate" value={item.rate} onChange={e => updateItem(idx, "rate", e.target.value)}
                      className="col-span-3 bg-zinc-900 border-zinc-700 text-white text-xs rounded-[2px]" />
                    <button onClick={() => removeItem(idx)} className="col-span-1 text-zinc-600 hover:text-red-400"><X className="w-3.5 h-3.5" /></button>
                  </div>
                ))}
              </div>
            </div>

            {/* Totals */}
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label className="text-zinc-400 text-xs uppercase tracking-wider">Tax Rate (%)</Label>
                <Input data-testid="invoice-tax-rate" type="number" value={form.tax_rate} onChange={e => {
                  const tr = Number(e.target.value);
                  const totals = calculateTotals(form.items, tr);
                  setForm({ ...form, tax_rate: tr, ...totals });
                }} className="mt-1 bg-zinc-900 border-zinc-700 text-white rounded-[2px]" />
              </div>
              <div>
                <Label className="text-zinc-400 text-xs uppercase tracking-wider">Status</Label>
                <select data-testid="invoice-status-select" value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}
                  className="mt-1 w-full h-10 px-3 bg-zinc-900 border border-zinc-700 text-white rounded-[2px] text-sm">
                  <option value="draft">Draft</option>
                  <option value="sent">Sent</option>
                  <option value="paid">Paid</option>
                  <option value="overdue">Overdue</option>
                </select>
              </div>
              <div>
                <Label className="text-zinc-400 text-xs uppercase tracking-wider">Due Date</Label>
                <Input data-testid="invoice-due-date" type="date" value={form.due_date} onChange={e => setForm({ ...form, due_date: e.target.value })}
                  className="mt-1 bg-zinc-900 border-zinc-700 text-white rounded-[2px]" />
              </div>
            </div>

            <div className="bg-zinc-900 rounded-sm p-3 space-y-1">
              <div className="flex justify-between text-xs text-zinc-400"><span>Subtotal</span><span className="font-mono">${form.subtotal.toFixed(2)}</span></div>
              <div className="flex justify-between text-xs text-zinc-400"><span>Tax ({form.tax_rate}%)</span><span className="font-mono">${form.tax_amount.toFixed(2)}</span></div>
              <div className="flex justify-between text-sm font-bold text-white border-t border-zinc-800 pt-1 mt-1"><span>Total</span><span className="font-mono">${form.total.toFixed(2)}</span></div>
            </div>

            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Notes</Label>
              <Textarea data-testid="invoice-notes" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })}
                className="mt-1 bg-zinc-900 border-zinc-700 text-white rounded-[2px]" rows={2} />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <button data-testid="cancel-invoice" onClick={() => setDialogOpen(false)}
                className="h-9 px-4 border border-zinc-700 text-zinc-300 text-xs font-medium uppercase tracking-wider rounded-[2px] hover:bg-zinc-800">Cancel</button>
              <button data-testid="save-invoice" onClick={handleSave}
                className="h-9 px-4 bg-orange-500 hover:bg-orange-600 text-white text-xs font-medium uppercase tracking-wider rounded-[2px] active:scale-95">
                {editing ? "Update" : "Create"} Invoice
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
